import React, { useEffect, useRef, useState } from 'react';
import { Button, InputPassword, SparkIcon, sparkToast, useReadOnly } from '@spark';
import EditableSecretInput from './EditableSecretInput.jsx';
import RecommendedProviderRow from './RecommendedProviderRow.jsx';
import ValidatingStatusText from './ValidatingStatusText.jsx';
import { useAuth } from '@/features/auth/AuthContext';
import { data_from_server_post } from '@/shared/http/client';
import './settings.css';

/**
 * Vendor API keys panel (Figma frames 6001-1017 / 6006-368): heading +
 * description with docs link, masked key rows, an inline InputPassword for
 * entering a new key (Enter saves — a "Press ↵ Enter to add" hint is always
 * shown below it, with animated validation status), a "+ Add New API Key" tiny ghost
 * button directly beneath the key input area, and the org-wide "Mark as
 * recommended provider" row.
 */
const ApiKey = ({
    apiKeys,
    onKeysUpdate,
    provider,
    title,
    descriptionPrefix,
    docsUrl,
    docsQuestion,
    placeholder,
    providerDisplayName,
    recommendedProvider,
    onRecommendedProviderUpdate,
    onDraftStateChange,
    submitDraftRef,
}) => {
    const { role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const { readOnly } = useReadOnly();
    const [newKey, setNewKey] = useState('');
    const [isAddingKey, setIsAddingKey] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const addInputRef = useRef(null);
    const pendingSubmitRef = useRef(null);

    // Let a parent (onboarding modal) mirror the draft key state, e.g. to
    // enable its Continue button as soon as a key is typed.
    useEffect(() => {
        onDraftStateChange?.({ hasDraft: Boolean(newKey.trim()), isValidating });
    }, [newKey, isValidating, onDraftStateChange]);

    useEffect(() => () => {
        onDraftStateChange?.({ hasDraft: false, isValidating: false });
    }, [onDraftStateChange]);

    const hasKeys = apiKeys.length > 0;
    const showAddInput = !hasKeys || isAddingKey;

    // Reset the inline input whenever the provider tab changes.
    useEffect(() => {
        setNewKey('');
        setIsAddingKey(false);
    }, [provider]);

    useEffect(() => {
        if (isAddingKey) {
            addInputRef.current?.focus();
        }
    }, [isAddingKey]);

    // Round-trip the candidate key through the backend, which builds an
    // ephemeral, non-registered ModelProvider and pings the vendor. The
    // backend never persists the key here; only on the subsequent
    // /save_llm_keys call do we write to OrganizationSettings.
    const validateKeyWithVendor = (key) => new Promise((resolve, reject) => {
        if (!provider) {
            // Defensive: ApiKey is also reachable from non-vendor flows;
            // skip validation rather than 400ing on an unknown provider.
            resolve();
            return;
        }
        data_from_server_post(
            "validate_vendor_api_key",
            { provider, api_key: key },
            (res) => {
                if (res && res.valid) resolve();
                else reject(new Error((res && res.error) || "Invalid API key"));
            },
            (err) => reject(new Error(err || "Validation request failed")),
        );
    });

    const submitNewKey = async () => {
        const candidate = (newKey || '').trim();
        // Detailed toasts match the bottom-right title + description + corner X
        // style (Figma Model Creation 3423-15453).
        if (!candidate) {
            sparkToast.error('API key required', {
                content: 'The API key cannot be empty',
                detailed: true,
            });
            return false;
        }
        if (candidate.length < 20) {
            sparkToast.error('API key too short', {
                content: 'The API key appears to be too short',
                detailed: true,
            });
            return false;
        }
        if (apiKeys.includes(candidate)) {
            sparkToast.error('Duplicate API key', {
                content: 'This API key is already in your list',
                detailed: true,
            });
            return false;
        }

        setIsValidating(true);
        try {
            await validateKeyWithVendor(candidate);
        } catch (e) {
            sparkToast.error(`Invalid ${providerDisplayName} API key`, {
                content: 'Please check the key and try again',
                detailed: true,
            });
            return false;
        } finally {
            setIsValidating(false);
        }

        setNewKey('');
        setIsAddingKey(false);
        onKeysUpdate([...apiKeys, candidate], 'saved');
        return true;
    };

    // Resolves true when the key passed validation and was saved. Deduped so
    // a Continue click while an Enter-triggered validation is in flight awaits
    // the same request instead of starting a second one.
    const handleAddKey = () => {
        if (!pendingSubmitRef.current) {
            pendingSubmitRef.current = submitNewKey().finally(() => {
                pendingSubmitRef.current = null;
            });
        }
        return pendingSubmitRef.current;
    };

    // Expose the submit flow so the onboarding modal's Continue button can
    // validate-and-save a typed key that was never confirmed with Enter.
    useEffect(() => {
        if (!submitDraftRef) return undefined;
        submitDraftRef.current = handleAddKey;
        return () => {
            submitDraftRef.current = null;
        };
    });

    const handleDeleteKey = (keyToDelete) => {
        const remaining = apiKeys.filter((key) => key !== keyToDelete);
        // A provider without credentials cannot stay recommended: clear the
        // org-wide recommendation together with the last deleted key.
        if (
            remaining.length === 0
            && recommendedProvider?.provider === provider
            && onRecommendedProviderUpdate
        ) {
            onRecommendedProviderUpdate(null);
        }
        onKeysUpdate(remaining, 'deleted');
    };

    const getMaskedKey = (key) => {
        if (!key || key.length <= 6) return '*'.repeat(key ? key.length : 0);
        return key.substring(0, 3) + '...' + key.substring(key.length - 4);
    };

    return (
        <div className="settings-providers__panel">
            <div className="settings-subheading-group">
                <h3 className="settings-subheading fs-unmask">{title}</h3>
                <p className="settings-text settings-providers__description fs-unmask">
                    {descriptionPrefix}
                    {' '}
                    {docsQuestion}
                    {' Get one '}
                    <a
                        className="settings-link"
                        href={docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        here
                    </a>
                    .
                </p>
            </div>

            {hasKeys ? (
                <div className="settings-keys__list">
                    {apiKeys.map((key, index) => (
                        <EditableSecretInput
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${provider}-${index}`}
                            value={key}
                            placeholder={placeholder}
                            maskFn={getMaskedKey}
                            editable={isAdmin}
                            showEdit={false}
                            copyInsideInput
                            confirmDeleteTitle="Delete this API key?"
                            confirmDeleteDescription={`This removes the ${providerDisplayName} API key from your organization.`}
                            onDelete={isAdmin ? () => handleDeleteKey(key) : undefined}
                        />
                    ))}
                </div>
            ) : null}

            {showAddInput ? (
                <div className="settings-keys__add">
                    <div className="settings-keys__row">
                        <InputPassword
                            ref={addInputRef}
                            size="large"
                            showLabel={false}
                            showPrefixIcon
                            value={newKey}
                            onChange={(e) => setNewKey(e.target.value)}
                            placeholder={placeholder}
                            disabled={readOnly || !isAdmin}
                            className="settings-secret__input"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isValidating) handleAddKey();
                            }}
                        />
                        {isValidating ? (
                            <ValidatingStatusText providerDisplayName={providerDisplayName} />
                        ) : null}
                        {isAddingKey && !isValidating ? (
                            <Button
                                size="tiny"
                                variant="ghost"
                                iconSlug="close"
                                aria-label="Cancel"
                                startIcon={<SparkIcon slug="close" size="small" />}
                                onClick={() => { setIsAddingKey(false); setNewKey(''); }}
                            />
                        ) : null}
                    </div>
                    {/* Always rendered so the layout doesn't jump when typing starts. */}
                    <div className="settings-keys__enter-hint">
                        Press ↵ Enter to add
                    </div>
                </div>
            ) : null}

            {hasKeys && isAdmin && !isAddingKey ? (
                <Button
                    size="tiny"
                    variant="ghost"
                    showStartIcon
                    disableWhenReadOnly
                    onClick={() => setIsAddingKey(true)}
                >
                    Add New API Key
                </Button>
            ) : null}

            <RecommendedProviderRow
                provider={provider}
                // With no keys the provider cannot be recommended: render
                // unchecked even if a stale server-side recommendation exists.
                recommendedProvider={hasKeys ? recommendedProvider : null}
                onRecommendedProviderUpdate={onRecommendedProviderUpdate}
                disabled={!hasKeys}
            />
        </div>
    );
};

export default ApiKey;
